import { equals } from 'ramda';
import { ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';

import { getOffers, getSuggestionIds } from 'shared/api/offers';
import { IAppState } from 'shared/common/state';
import { getGeoTagPromise } from 'shared/filters/api';
import { IOffer } from 'shared/packages/search-card-fragment/types/offer';
import { useApplicationContext } from 'shared/serp/containers/ApplicationContext';
import {
  ICoordinatesOffers,
  requestSuggestionsDistances,
  setQsToUris,
  setSuggestionDistancesSeoText,
} from 'shared/serp/state/suggestions';
import { getSuggestionDistancesSeoText } from 'shared/utils/seo_text';

import { SuggestionsLayout } from './components/SuggestionsLayout';
import { useSuggestionsOffersRenderer } from './useSuggestionsOffersRenderer';
import { useSuggestionsTracking } from './useSuggestionsTracking';

export function SuggestionsContainer() {
  const ctx = useApplicationContext();

  const dispatch = useDispatch<Dispatch<AnyAction>>();

  const offers = useSelector((state: IAppState) => state.results.offers);
  const suggestions = useSelector((state: IAppState) => state.results.suggestions);
  const makeRequest = useSelector((state: IAppState) => state.makeRequest);
  const searchOffersApiBaseUrl = useSelector((state: IAppState) => state.searchOffersApiBaseUrl);
  const jsonQuery = useSelector((state: IAppState) => state.results.jsonQuery);
  const queryString = useSelector((state: IAppState) => state.results.queryString);
  const directions = useSelector((state: IAppState) => state.filters.directionsModal);
  const fullUrl = useSelector((state: IAppState) => state.results.fullUrl);
  const liquiditySource = useSelector((state: IAppState) => state.liquiditySource);

  const suggestionsRef = useRef<IOffer[] | null>(null);
  const mountedRef = useRef<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);
  const [nextStepSuggestions, setNextStepSuggestions] = useState<number[]>([]);
  const [additionalSuggestions, setAdditionalSuggestions] = useState<IOffer[][]>([]);
  const [modelVersion, setModelVersion] = useState(0);

  const renderCards = useSuggestionsOffersRenderer();
  const handleTrackSuggestions = useSuggestionsTracking();

  const isSeoUrl = useMemo(() => {
    const { pathname } = new URL(fullUrl);

    return !pathname.startsWith('/cat.php');
  }, [fullUrl]);

  const visibleSuggestionsNodes = useMemo<ReactNode[]>(() => {
    return [
      ...renderCards({
        pageNumber: 1,
        offers: suggestions ?? [],
      }),
      ...additionalSuggestions.map((additionalSuggestions, additionalSuggestionsPage) => {
        return renderCards({
          offset: suggestions?.length ?? 0,
          pageNumber: additionalSuggestionsPage + 1,
          offers: additionalSuggestions,
        });
      }),
    ];
  }, [additionalSuggestions, renderCards, suggestions]);

  const allVisibleOffersIds = useMemo(
    () => [
      ...offers.map(offer => offer.id),
      ...(suggestions || []).map(suggestion => suggestion.id),
      ...additionalSuggestions.flat().map(suggestion => suggestion.id),
    ],
    [offers, suggestions, additionalSuggestions],
  );

  /**
   * Функция для получения id предложений для следующей страницы
   */
  const getNextStepSuggestions = useCallback(async () => {
    try {
      const response = await getSuggestionIds(makeRequest, searchOffersApiBaseUrl, {
        jsonQuery,
        queryString,
        offerIds: allVisibleOffersIds,
        pageNumber: additionalSuggestions.length + 1,
      });

      const nextStepSuggestions = response.map(searchResult => searchResult.itemId);

      setNextStepSuggestions(nextStepSuggestions);
    } catch (_error) {
      const dataError = new Error('Failed get-infinite-search-result-desktop');
      ctx.logger.error(dataError);
    }
  }, [
    additionalSuggestions.length,
    allVisibleOffersIds,
    ctx.logger,
    jsonQuery,
    makeRequest,
    queryString,
    searchOffersApiBaseUrl,
  ]);

  /**
   * Функция для загрузки офферов по id, а так же новых id предложений
   */
  const handleLoadMoreClick = useCallback(
    async (event: SyntheticEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      const nextAllVisibleOffersIds = [...allVisibleOffersIds, ...nextStepSuggestions];

      setIsLoading(true);

      const nextStepDataPromise = getSuggestionIds(makeRequest, searchOffersApiBaseUrl, {
        jsonQuery,
        queryString,
        offerIds: nextAllVisibleOffersIds.length > 100 ? nextAllVisibleOffersIds.slice(-100) : nextAllVisibleOffersIds,
        pageNumber: additionalSuggestions.length + 1,
      });

      const nextStepOffersPromise = getOffers(
        makeRequest,
        searchOffersApiBaseUrl,
        nextStepSuggestions,
        jsonQuery,
        liquiditySource,
      );

      try {
        const [nextStepData, nextStepOffers] = await Promise.all([nextStepDataPromise, nextStepOffersPromise]);

        const { offersSerialized: offers, qsToUris } = nextStepOffers;
        const newNextStepSuggestionsIds = nextStepData.map(nextStepData => nextStepData.itemId);

        const newModelVersion = nextStepData && nextStepData.length > 0 ? nextStepData[0].modelVersion : null;

        if (newModelVersion) {
          setModelVersion(newModelVersion);
        }

        dispatch(setQsToUris(qsToUris));

        const newSuggestions = offers.map(offer => ({ ...offer, modelVersion: newModelVersion || modelVersion }));

        setAdditionalSuggestions(prevState => [...prevState, newSuggestions]);

        handleTrackSuggestions(
          suggestions,
          additionalSuggestions.length + 1,
          [...(suggestions || []), ...additionalSuggestions.flat(), ...newSuggestions],
          newSuggestions,
        );

        setIsLoading(false);
        setNextStepSuggestions(newNextStepSuggestionsIds);

        const coordinatesOffers = additionalSuggestions
          .flat()
          .map(suggestion => {
            const { geo } = suggestion;

            if (!geo) {
              return null;
            }

            return {
              offerId: suggestion.id,
              coordinates: {
                lat: geo.coordinates.lat,
                lng: geo.coordinates.lng,
              },
            };
          })
          .filter(Boolean);

        if (coordinatesOffers.length > 0) {
          dispatch(requestSuggestionsDistances(coordinatesOffers as ICoordinatesOffers[]));
        }
      } catch (error) {
        const dataError = new Error(
          `Failed to get additional suggestions info,
      suggestions ids: ${nextStepSuggestions}
      next suggestions ids: ${nextAllVisibleOffersIds}`,
        );
        ctx.logger.error(dataError);

        setIsLoading(false);
      }
    },
    [
      additionalSuggestions,
      allVisibleOffersIds,
      ctx.logger,
      dispatch,
      handleTrackSuggestions,
      jsonQuery,
      makeRequest,
      modelVersion,
      nextStepSuggestions,
      queryString,
      searchOffersApiBaseUrl,
      suggestions,
      liquiditySource,
    ],
  );

  const updateSuggestionDistancesSeoText = useCallback(async () => {
    let suggestionDistancesSeoText = '';

    try {
      const geoTagsData = await getGeoTagPromise(ctx.httpApi, ctx.logger, jsonQuery, ctx.custom.subdomain);
      // Без гео-тегов оставляем текст, посчитанный SSR: пустой стирал SEO-контент добивок
      if (!geoTagsData) {
        return;
      }

      const isPersonToPersonURL = window.location.pathname !== '/cat.php';
      if (isPersonToPersonURL) {
        suggestionDistancesSeoText = getSuggestionDistancesSeoText(geoTagsData, directions && directions.data);
      }
      dispatch(setSuggestionDistancesSeoText(suggestionDistancesSeoText));
    } catch {
      // У поискового бота запрос закрыт в robots.txt: оставляем текст, посчитанный SSR
    }
  }, [dispatch, directions, jsonQuery, makeRequest]);

  useEffect(() => {
    if (!equals(suggestions, suggestionsRef.current)) {
      if (mountedRef.current) {
        /**
         * Если компонент уже был отрисован, то обнуляем дополнительные предложения и версию модели
         */
        setAdditionalSuggestions([]);
        setNextStepSuggestions([]);
        setModelVersion(0);
      }

      /**
       * При первом показе и при изменении поиска отправляем аналитику по добивкам пришедшим с бекенда внутри search-offers-desktop с page_num=0
       */
      handleTrackSuggestions(suggestions, 0);
      /**
       * Получение id предложений для следующей страницы
       */
      getNextStepSuggestions();

      // Получение текста для SEO и расстояние до текущего гео только
      if (suggestions?.length && isSeoUrl) {
        updateSuggestionDistancesSeoText();
        dispatch(requestSuggestionsDistances());
      }

      suggestionsRef.current = suggestions;
    }
  }, [
    dispatch,
    getNextStepSuggestions,
    handleTrackSuggestions,
    isSeoUrl,
    suggestions,
    updateSuggestionDistancesSeoText,
  ]);

  if (!suggestions?.length && !additionalSuggestions.length) {
    return null;
  }

  return (
    <SuggestionsLayout
      content={visibleSuggestionsNodes}
      hasMoreOffers={nextStepSuggestions.length > 0}
      isLoading={isLoading}
      title={offers.length > 0 ? 'Дополнительные предложения по вашему запросу' : 'Похожие предложения'}
      onShowMoreClick={handleLoadMoreClick}
    />
  );
}
